import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { supabase } from "../lib/supabaseClient";

const colors = {
    cream: "#fffaf2",
    paper: "rgba(255, 250, 242, 0.94)",
    ink: "#2a2621",
    muted: "rgba(42, 38, 33, 0.64)",
    softMuted: "rgba(42, 38, 33, 0.48)",
    brown: "#9b7653",
    brownDark: "#6f5136",
    brownDarker: "#543b26",
    border: "rgba(155, 118, 83, 0.18)",
    errorBg: "rgba(211, 47, 47, 0.08)",
};

function AdminLogin() {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    async function handleSubmit(event) {
        event.preventDefault();

        const trimmedEmail = email.trim();

        if (!trimmedEmail || !password) {
            setErrorMessage("Bitte E-Mail und Passwort eingeben.");
            return;
        }

        setIsSubmitting(true);
        setErrorMessage("");

        const { error } = await supabase.auth.signInWithPassword({
            email: trimmedEmail,
            password,
        });

        setIsSubmitting(false);

        if (error) {
            setErrorMessage("Login fehlgeschlagen. Bitte Zugangsdaten prüfen.");
            return;
        }

        navigate("/admin");
    }

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                position: "relative",
                overflow: "hidden",
                background:
                    "radial-gradient(circle at 15% 20%, rgba(155,118,83,0.18) 0%, rgba(155,118,83,0) 30%), radial-gradient(circle at 85% 15%, rgba(111,81,54,0.12) 0%, rgba(111,81,54,0) 34%), linear-gradient(135deg, #fffaf2 0%, #f7f1e8 48%, #edf7fa 100%)",
                py: {
                    xs: 4,
                    md: 8,
                },
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    width: 360,
                    height: 360,
                    borderRadius: "50%",
                    background: "rgba(155,118,83,0.12)",
                    filter: "blur(18px)",
                    top: -140,
                    right: -120,
                }}
            />

            <Box
                aria-hidden="true"
                sx={{
                    position: "absolute",
                    width: 260,
                    height: 260,
                    borderRadius: "50%",
                    background: "rgba(238,247,250,0.9)",
                    filter: "blur(14px)",
                    bottom: -90,
                    left: -70,
                }}
            />

            <Container
                maxWidth="lg"
                sx={{
                    position: "relative",
                    zIndex: 1,
                    px: {
                        xs: 2,
                        sm: 3,
                    },
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "0.95fr 1.05fr",
                        },
                        overflow: "hidden",
                        borderRadius: {
                            xs: 4,
                            sm: 6,
                        },
                        border: `1px solid ${colors.border}`,
                        backgroundColor: colors.paper,
                        boxShadow:
                            "0 30px 90px rgba(78,54,30,0.16), 0 2px 12px rgba(78,54,30,0.06)",
                        backdropFilter: "blur(18px)",
                    }}
                >
                    <Box
                        sx={{
                            display: {
                                xs: "none",
                                md: "flex",
                            },
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: 620,
                            p: 5,
                            color: "#fff",
                            background:
                                "linear-gradient(145deg, rgba(111,81,54,0.96) 0%, rgba(84,59,38,0.98) 100%)",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    mb: 2,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 13,
                                    fontWeight: 900,
                                    letterSpacing: "0.18em",
                                    textTransform: "uppercase",
                                    opacity: 0.78,
                                }}
                            >
                                Adminbereich
                            </Typography>

                            <Typography
                                component="h1"
                                sx={{
                                    maxWidth: 420,
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: {
                                        md: "3.6rem",
                                    },
                                    lineHeight: 0.95,
                                    fontWeight: 800,
                                    letterSpacing: "-0.06em",
                                }}
                            >
                                Inhalte sicher verwalten.
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 3,
                                    maxWidth: 390,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 17,
                                    lineHeight: 1.7,
                                    opacity: 0.78,
                                }}
                            >
                                Melde dich an, um Neuigkeiten, Inhalte und Einstellungen
                                im geschützten Bereich zu bearbeiten.
                            </Typography>
                        </Box>

                        <Stack spacing={2.2}>
                            {[
                                "Geschützter Zugang",
                                "Schnelle Verwaltung",
                                "Klares Admin-Dashboard",
                            ].map((item) => (
                                <Box
                                    key={item}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            backgroundColor: "#fff",
                                            opacity: 0.8,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontFamily: '"Nunito Sans", sans-serif',
                                            fontSize: 15,
                                            fontWeight: 800,
                                            opacity: 0.9,
                                        }}
                                    >
                                        {item}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            p: {
                                xs: 3,
                                sm: 5,
                                md: 7,
                            },
                        }}
                    >
                        <Box sx={{ width: "100%" }}>
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 54,
                                    height: 54,
                                    mb: 3,
                                    borderRadius: 3,
                                    backgroundColor: "rgba(155,118,83,0.12)",
                                    color: colors.brownDark,
                                }}
                            >
                                <Typography
                                    aria-hidden="true"
                                    sx={{
                                        fontSize: 24,
                                        fontWeight: 900,
                                        lineHeight: 1,
                                    }}
                                >
                                    🔒
                                </Typography>
                            </Box>

                            <Typography
                                component="p"
                                sx={{
                                    mb: 1,
                                    display: {
                                        xs: "block",
                                        md: "none",
                                    },
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 12,
                                    fontWeight: 900,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.brown,
                                }}
                            >
                                Adminbereich
                            </Typography>

                            <Typography
                                component="h2"
                                sx={{
                                    mb: 1.2,
                                    fontFamily: '"Quicksand", sans-serif',
                                    fontSize: {
                                        xs: "2.3rem",
                                        sm: "3rem",
                                    },
                                    lineHeight: 1,
                                    fontWeight: 800,
                                    letterSpacing: "-0.055em",
                                    color: colors.ink,
                                }}
                            >
                                Willkommen zurück
                            </Typography>

                            <Typography
                                sx={{
                                    mb: 3.5,
                                    maxWidth: 480,
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 16,
                                    lineHeight: 1.7,
                                    color: colors.muted,
                                }}
                            >
                                Bitte melde dich mit deinen Admin-Zugangsdaten an.
                            </Typography>

                            {errorMessage && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        mb: 2.5,
                                        borderRadius: 3,
                                        backgroundColor: colors.errorBg,
                                        fontFamily: '"Nunito Sans", sans-serif',
                                        "& .MuiAlert-message": {
                                            fontWeight: 700,
                                        },
                                    }}
                                >
                                    {errorMessage}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <Stack spacing={2.2}>
                                    <TextField
                                        label="E-Mail-Adresse"
                                        type="email"
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        fullWidth
                                        required
                                        autoComplete="email"
                                        disabled={isSubmitting}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography
                                                        aria-hidden="true"
                                                        sx={{
                                                            color: colors.softMuted,
                                                            fontSize: 18,
                                                            fontWeight: 900,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        @
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: "rgba(255,255,255,0.72)",
                                                fontFamily: '"Nunito Sans", sans-serif',

                                                "& fieldset": {
                                                    borderColor: "rgba(42,38,33,0.14)",
                                                },

                                                "&:hover fieldset": {
                                                    borderColor: "rgba(155,118,83,0.45)",
                                                },

                                                "&.Mui-focused fieldset": {
                                                    borderColor: colors.brown,
                                                    borderWidth: 2,
                                                },
                                            },

                                            "& .MuiInputLabel-root": {
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontWeight: 700,
                                            },
                                        }}
                                    />

                                    <TextField
                                        label="Passwort"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}
                                        fullWidth
                                        required
                                        autoComplete="current-password"
                                        disabled={isSubmitting}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography
                                                        aria-hidden="true"
                                                        sx={{
                                                            color: colors.softMuted,
                                                            fontSize: 15,
                                                            fontWeight: 900,
                                                            letterSpacing: "-0.04em",
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        •••
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        type="button"
                                                        edge="end"
                                                        aria-label={
                                                            showPassword
                                                                ? "Passwort ausblenden"
                                                                : "Passwort anzeigen"
                                                        }
                                                        onClick={() =>
                                                            setShowPassword((current) => !current)
                                                        }
                                                        disabled={isSubmitting}
                                                        sx={{
                                                            borderRadius: 2,
                                                            px: 1.2,
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: 13,
                                                                fontWeight: 900,
                                                                color: colors.brownDark,
                                                                fontFamily:
                                                                    '"Nunito Sans", sans-serif',
                                                            }}
                                                        >
                                                            {showPassword
                                                                ? "Ausblenden"
                                                                : "Anzeigen"}
                                                        </Typography>
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: 3,
                                                backgroundColor: "rgba(255,255,255,0.72)",
                                                fontFamily: '"Nunito Sans", sans-serif',

                                                "& fieldset": {
                                                    borderColor: "rgba(42,38,33,0.14)",
                                                },

                                                "&:hover fieldset": {
                                                    borderColor: "rgba(155,118,83,0.45)",
                                                },

                                                "&.Mui-focused fieldset": {
                                                    borderColor: colors.brown,
                                                    borderWidth: 2,
                                                },
                                            },

                                            "& .MuiInputLabel-root": {
                                                fontFamily: '"Nunito Sans", sans-serif',
                                                fontWeight: 700,
                                            },
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={isSubmitting}
                                        fullWidth
                                        sx={{
                                            mt: 0.5,
                                            minHeight: 56,
                                            borderRadius: 999,
                                            backgroundColor: colors.brownDark,
                                            color: "#fff",
                                            fontFamily: '"Nunito Sans", sans-serif',
                                            fontSize: 14,
                                            fontWeight: 900,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                            boxShadow:
                                                "0 16px 34px rgba(111,81,54,0.26)",

                                            "&:hover": {
                                                backgroundColor: colors.brownDarker,
                                                boxShadow:
                                                    "0 18px 38px rgba(111,81,54,0.32)",
                                            },

                                            "&:disabled": {
                                                color: "rgba(255,255,255,0.78)",
                                                backgroundColor: "rgba(111,81,54,0.62)",
                                            },
                                        }}
                                    >
                                        {isSubmitting ? "Anmeldung läuft..." : "Einloggen"}
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider
                                sx={{
                                    my: 3,
                                    borderColor: "rgba(42,38,33,0.1)",
                                }}
                            />

                            <Typography
                                sx={{
                                    fontFamily: '"Nunito Sans", sans-serif',
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    color: colors.softMuted,
                                }}
                            >
                                Dieser Bereich ist nur für autorisierte Personen vorgesehen.
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default AdminLogin;